/**
 * Phase 6.2 — Coordinator / admin moderation queue.
 *
 * Mounted as a sub-tab inside the participant Care Plan area. Hidden when
 * `portal_comments_enabled` is OFF.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle2, XCircle, MessageSquare, Filter, Clock, RefreshCw,
} from 'lucide-react';
import api from '@/lib/api';

const STATUSES = ['pending', 'approved', 'rejected', 'withdrawn'];
const TARGET_KINDS = [
  ['care_plan_section', 'Care plan section'],
  ['care_plan_structured_row', 'Care plan row'],
  ['participant_goal', 'Goal'],
  ['goal_outcome', 'Outcome'],
];

export function CoordinatorCommentModeration({ participantId }) {
  const [available, setAvailable] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [status, setStatus] = useState('pending');
  const [targetKind, setTargetKind] = useState('');
  const [noteDrafts, setNoteDrafts] = useState({});

  const fetchQueue = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (participantId) params.participant_id = participantId;
      if (status) params.status = status;
      if (targetKind) params.target_kind = targetKind;
      const r = await api.get('/comments/moderation', { params });
      setItems(r.data?.data?.items || []);
      setPendingCount(r.data?.data?.pending_count || 0);
      setAvailable(true);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) setAvailable(false);
      else setError(e?.response?.data?.detail || e.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [participantId, status, targetKind]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const moderate = useCallback(async (id, action) => {
    try {
      await api.post(`/comments/${id}/moderate`, {
        action,
        moderation_note: noteDrafts[id] || undefined,
      });
      setNoteDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      fetchQueue();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Moderation action failed');
    }
  }, [noteDrafts, fetchQueue]);

  const targetLabel = useMemo(() => {
    const m = new Map(TARGET_KINDS);
    return (k) => m.get(k) || k;
  }, []);

  if (!available) return null;

  return (
    <div className="space-y-3" data-testid="coordinator-comment-moderation">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Portal Comments</h3>
          <Badge
            variant="outline"
            className="bg-amber-50 border-amber-200 text-amber-900"
            data-testid="coordinator-comment-pending-count"
          >
            {pendingCount} pending
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-3 w-3 text-muted-foreground" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-7 text-xs w-[140px]" data-testid="coordinator-comment-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={targetKind || 'all'} onValueChange={(v) => setTargetKind(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-7 text-xs w-[180px]" data-testid="coordinator-comment-filter-target">
              <SelectValue placeholder="All target types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All target types</SelectItem>
              {TARGET_KINDS.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            size="sm" variant="outline" onClick={fetchQueue} disabled={loading}
            data-testid="coordinator-comment-refresh"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {error && <p className="text-xs text-rose-600" data-testid="coordinator-comment-error">{String(error)}</p>}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground"
                       data-testid="coordinator-comment-empty">
            No comments in this view.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((c) => (
            <li key={c.id}>
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-xs flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      {targetLabel(c.target_kind)}
                      <span className="font-mono text-[10px] text-muted-foreground">{c.target_id}</span>
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px]"
                           data-testid={`coordinator-comment-status-${c.id}`}>
                      {c.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm whitespace-pre-wrap break-words border rounded-md p-2 bg-muted/30"
                     data-testid={`coordinator-comment-body-${c.id}`}>
                    {c.body}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {c.author_role} · v{c.version_no} · {new Date(c.created_at).toLocaleString()}
                  </p>
                  {c.status === 'pending' && (
                    <div className="mt-2 space-y-2">
                      <textarea
                        rows={2}
                        placeholder="Optional internal moderation note (not shown to portal users)"
                        value={noteDrafts[c.id] || ''}
                        onChange={(e) => setNoteDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                        className="w-full text-xs rounded-md border bg-background px-2 py-1"
                        data-testid={`coordinator-comment-note-${c.id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => moderate(c.id, 'approve')}
                                data-testid={`coordinator-comment-approve-${c.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => moderate(c.id, 'reject')}
                                data-testid={`coordinator-comment-reject-${c.id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}
                  {c.status !== 'pending' && c.moderation_note && (
                    <p className="text-[11px] text-muted-foreground mt-2">
                      <span className="font-semibold">Note:</span> {c.moderation_note}
                    </p>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CoordinatorCommentModeration;
